/**
 * Client-Side Emergency Vault & Local Backup Utility
 * Automatically maintains persistent emergency backups of site config and products
 * directly in browser localStorage / IndexedDB.
 */

const VAULT_PREFIX = 'omnivaso_emergency_vault_';
const MAX_VAULT_ITEMS = 3;

/**
 * Save snapshot of current config and products to browser vault
 */
export const saveToLocalVault = (tenantHandle, config, products = null, orders = null, categories = null) => {
    try {
        if (!config || typeof config !== 'object') return false;
        const tenant = tenantHandle || 'default';
        const key = `${VAULT_PREFIX}${tenant}`;
        
        const timestamp = Date.now();

        // Create lightweight summaries to save browser storage space
        const lightProducts = Array.isArray(products) 
            ? products.slice(0, 30).map(p => ({ id: p.id, name: p.name, price: p.price, category: p.category })) 
            : null;

        const lightOrders = Array.isArray(orders)
            ? orders.slice(0, 30).map(o => ({ id: o.id, date: o.date, total: o.total, status: o.status }))
            : null;

        const newEntry = {
            id: `local_vault_${timestamp}_${Math.random().toString(36).substring(2, 7)}`,
            timestamp,
            dateFormatted: new Date(timestamp).toLocaleString(),
            siteTitle: config?.settings?.siteTitle || 'Omnivaso Site',
            sectionsCount: (config?.sections || []).length,
            pagesCount: (config?.pages || []).length,
            productsCount: Array.isArray(products) ? products.length : 0,
            ordersCount: Array.isArray(orders) ? orders.length : 0,
            categoriesCount: Array.isArray(categories) ? categories.length : 0,
            config,
            products: lightProducts,
            orders: lightOrders,
            categories
        };

        const existingRaw = localStorage.getItem(key);
        let entries = [];
        if (existingRaw) {
            try {
                entries = JSON.parse(existingRaw);
                if (!Array.isArray(entries)) entries = [];
            } catch (e) { entries = []; }
        }

        // Avoid adding duplicate identical config snapshots within 1 minute
        if (entries.length > 0) {
            const last = entries[0];
            if ((timestamp - last.timestamp) < 60000 && JSON.stringify(last.config) === JSON.stringify(config)) {
                return true;
            }
        }

        entries.unshift(newEntry);
        if (entries.length > MAX_VAULT_ITEMS) {
            entries = entries.slice(0, MAX_VAULT_ITEMS);
        }

        // Try saving with progressive fallbacks if LocalStorage quota is exceeded
        for (let count = entries.length; count >= 1; count--) {
            try {
                localStorage.setItem(key, JSON.stringify(entries.slice(0, count)));
                return true;
            } catch (quotaErr) {
                if (count === 1) {
                    // Try saving config without products if quota is extremely constrained
                    try {
                        const ultraLight = [{ ...newEntry, products: null }];
                        localStorage.setItem(key, JSON.stringify(ultraLight));
                        return true;
                    } catch (e2) {
                        // Silent fallback when browser storage quota is completely full
                        return false;
                    }
                }
            }
        }
        return false;
    } catch (err) {
        return false;
    }
};

/**
 * Get all stored local vault snapshots for a tenant
 */
export const getLocalVaultSnapshots = (tenantHandle) => {
    try {
        const tenant = tenantHandle || 'default';
        const key = `${VAULT_PREFIX}${tenant}`;
        const raw = localStorage.getItem(key);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
        return [];
    }
};

/**
 * Clear vault for tenant
 */
export const clearLocalVault = (tenantHandle) => {
    try {
        const tenant = tenantHandle || 'default';
        localStorage.removeItem(`${VAULT_PREFIX}${tenant}`);
        return true;
    } catch (e) {
        return false;
    }
};
